import {BaseElement, BASIS, Label, OLabelInit} from "/@back@/commons/basis.js";
import {MxFormElement, OFormElementInit} from "/@lib@/commons/forms.js";
import {REG} from "/@lib@/commons/registry.js";
import {DOM, JSX, ShadowJsx} from "/@lib@/commons/xml/dom.js";
import {ActionBtn, Button} from "/@back@/commons/widgets/buttons.js";
import {DOMSH} from "/@lib@/commons/xml/domsh.js";
import {Grid, GridSmall, OGridSmallInit} from "/@back@/commons/widgets/grid-tags.js";
import {CellBuilderIconLabel, CellBuilderLabel, CellBuilderString, GridDataHolderJsonArray, GridDataRowJson} from "/@back@/commons/widgets/grid-libs.js";
import {EGridDropPos, GridColDef, IGrid, IGridDataRow, IGridLineDrawer} from "/@back@/commons/widgets/grid-core.js";
import {POPUP} from "/@back@/commons/widgets/popups.js";
import {Action} from "/@lib@/commons/actions.js";
import {IPopupable} from "/@back@/commons/widgets/popupable.js";

export type IOrderedSetEntry = {
	label: string;
	icon?: string;
	iconFilter?: string;
	description?: string,
	/* Entrée inconnue */
	notAvailable?: boolean,
};

export interface OOrderedSetInit extends OFormElementInit {
	/** Valeur initiale du champ */
	value?: string[];
	/** Ensemble des valeurs disponibles */
	dataSet: Dict<IOrderedSetEntry>;
	/** Modalité de visualisation des valeurys courantes. horizontal par défaut */
	previewMode?: 'asLines' | 'horizontal'
	/** action permettant de créer une nouvelle valeur, pas encore présente dans le dataSet */
	createNewValueAction?: Action<InputOrderedSet>
	/** Message affiché lorsqu'aucune valeur n'est sélectionné */
	emptySelectionMsg?: string;
	/** Label affiché au dessus de la grille des valeurs sélectionnées */
	selectionLabel: string | OLabelInit;
	/** Label affiché au dessus de la grille des valeurs disponible */
	availableLabel: string | OLabelInit;
	/** LineDrawer de la grille des valeurs sélectionnées */
	selectionGridInit?: OGridSmallInit;
	/** LineDrawer de la grille des valeurs disponible */
	availableGridInit?: OGridSmallInit;
	/**  Fction évaluant la validité de la sélection
	 * @param selectedEntries
	 * @return true si valide; false|string si non valide
	 */
	checkValidity?: (this: InputOrderedSetPanel, selectedEntries?: string[]) => boolean | string
}

type IOrderedSetData = { value: string } & IOrderedSetEntry;

/**
 * Input de sélection d'un ensemble ordonné de valeur dans un ensemble donné (le dataSet)
 */
export class InputOrderedSet extends MxFormElement(BaseElement) implements IGridLineDrawer {
	protected _leftBtn: Button;
	protected _rightBtn: Button;
	protected _upBtn: Button;
	protected _downBtn: Button;
	protected _addAllBtn: Button;
	protected _removeAllBtn: Button;

	protected _params: OOrderedSetInit;

	/** Grille des valeurs disponibles */
	protected _availableGrid: GridSmall;

	/** Grille des valeurs sélectionnées */
	protected _selectionGrid: GridSmall;

	/** DataHolder des valeurs disponibles */
	protected _available = new GridDataHolderJsonArray<IOrderedSetData>();

	/** DataHolder des valeurs sélectionnées */
	protected _selection = new GridDataHolderJsonArray<IOrderedSetData>();

	/** Flag permettant de détecter l'origine du drag */
	protected dragFrom: null | GridSmall;

	protected _dataSet: Dict<IOrderedSetEntry>;

	get value(): string[] {
		return this._selection.getDatas().map((data) => data.value);
	}

	set value(vals: string[]) {
		const selection: IOrderedSetData[] = [];
		const available: IOrderedSetData[] = [];

		if (vals) for (const val of vals) {
			if (val in this._dataSet) selection.push(Object.assign({value: val}, this._dataSet[val]));
			else selection.push({value: val, label: val, notAvailable: true});
		}

		for (const entryKey in this._dataSet) {
			if (!vals || !vals.includes(entryKey)) available.push(Object.assign({value: entryKey}, this._dataSet[entryKey]));
		}

		this._available.setDatas(available);
		this._selection.setDatas(selection);
	}

	get dataSet(): Dict<IOrderedSetEntry> {
		return this._dataSet;
	}

	set dataSet(dataSet: Dict<IOrderedSetEntry>) {
		const val = this.value;
		const availables = [];
		for (const entryKey in dataSet) {
			const data = Object.assign({value: entryKey}, dataSet[entryKey]);
			availables.push(data);
		}
		this._available.setDatas(availables);
		this._selection.setDatas([]);
		this._dataSet = dataSet;
		this.value = val;
	}

	formDisabledCallback(disabled: boolean) {
		/* Désactivation des grid par stylage : ajout d'un attribut */
		DOM.setAttrBool(this._availableGrid, 'disabled', disabled);
		DOM.setAttrBool(this._selectionGrid, 'disabled', disabled);
		this._leftBtn.disabled = this._rightBtn.disabled = this._upBtn.disabled = this._downBtn.disabled = disabled;
		this._addAllBtn.disabled = this._removeAllBtn.disabled = disabled;
	}

	protected _initialize(init: OOrderedSetInit) {
		this._params = init;
		this.dataSet = init.dataSet;
		if (init.value) this.value = init.value;

		const reg = this.findReg(init);

		/* CellBuilder différent en fonction de la présence d'icône dans le dataSet */
		const hasIcon = Object.values(this._dataSet).some((data) => data.icon);
		const cellBuilder = hasIcon ? new CellBuilderIconLabel('label').setIconKey('icon').override("getIconFilter", (row: IGridDataRow) => {
			return row.getData("iconFilter")
		}) : new CellBuilderLabel('label');
		cellBuilder.setDescriptionFunc((row) => row.getData('description'));

		const selCellBuilderIndex = new CellBuilderString('index')
			.override("redrawCell", (row: IGridDataRow, root: HTMLElement) => {
				const grid = DOMSH.findHost<GridSmall>(root);
				root.appendChild(<span class="cellIndex">{(grid.dataHolder.getOffset(row.rowKey) + 1) + "#"}</span>);
			});

		/* Initialisation de la grille des valeurs disponible qui diffère par un tri implicite */
		this._availableGrid = new GridSmall().initialize(Object.assign({
			selType: 'multi',
			columnDefs: [new GridColDef('value')
				.setDefaultSort(1, 'ascendant')
				.setFlex('1rem', 1, 1)
				.setCellBuilder(cellBuilder)
			],
			dataHolder: this._available,
			hideHeaders: true,
			lineDrawer: this,
			skinOver: 'c-input-ordered-set/grid',
			skinScroll: 'scroll/small',
			noResizableCol: true,
		}, init.availableGridInit));
		this._availableGrid.id = 'availableGrid';
		this._availableGrid.addEventListener("grid-select", this._onGridSelect);
		this._initDrag(this._availableGrid);

		/* Initialisation de la grille des valeurs sélectionnées */
		this._selectionGrid = new GridSmall().initialize(Object.assign({
			selType: 'multi',
			columnDefs: [
				new GridColDef('index')
					.setFlex('1.3em', 0, 0)
					.setCellBuilder(selCellBuilderIndex),
				new GridColDef('value')
					.setFlex('1rem', 1, 1)
					.setCellBuilder(cellBuilder)
			],
			dataHolder: this._selection,
			hideHeaders: true,
			lineDrawer: this,
			skinOver: 'c-input-ordered-set/grid',
			skinScroll: 'scroll/small',
			noResizableCol: true,
			emptyBody: (): Node => document.createTextNode(typeof init.emptySelectionMsg == "string" ? init.emptySelectionMsg : "Aucune sélection")
		}, init.selectionGridInit));
		this._selectionGrid.id = 'selectionGrid';
		this._selectionGrid.addEventListener("grid-select", this._onGridSelect);
		this._initDrag(this._selectionGrid);

		this._rightBtn = <c-button î={{title: "Ajouter", icon: "/@skin@/commons/icons/right.svg"}} onclick={this._onRight}/> as Button;
		this._leftBtn = <c-button î={{title: "Enlever", icon: "/@skin@/commons/icons/left.svg"}} onclick={this._onLeft}/> as Button;
		this._upBtn = <c-button î={{title: "Haut", icon: "/@skin@/commons/icons/up.svg"}} onclick={this._onUp}/> as Button;
		this._downBtn = <c-button î={{title: "Bas", icon: "/@skin@/commons/icons/down.svg"}} onclick={this._onDown}/> as Button;

		this._selectionGrid.uiEvents.on("rowDblclick", this._onDblClick);
		this._availableGrid.uiEvents.on("rowDblclick", this._onDblClick);

		const newBtn = init.createNewValueAction ? ActionBtn.buildButton(init.createNewValueAction.setIcon('/@skin@/commons/icons/new.svg'), this, 'bar') : null;
		if (newBtn) newBtn.id = 'createValueBtn';

		const toolbar = <div id="toolbar">
			{this._rightBtn}
			{this._leftBtn}
			<div class="spacer"/>
			{this._upBtn}
			{this._downBtn}
		</div> as HTMLDivElement;

		let availableHead = <div id="availableHead" class="head"/>;
		let availableLabel;
		if (typeof init.availableLabel == "string") availableLabel = new Label().initialize({label: init.availableLabel});
		else availableLabel = new Label().initialize(init.availableLabel);
		availableHead.appendChild(availableLabel);

		this._addAllBtn = <c-button î={{icon: "/@skin@/commons/icons/doubleRight.svg", title: "Tout ajouter"}} class="allAction" onclick={this._addAll}/> as Button;
		availableHead.appendChild(this._addAllBtn);

		let selectionHead = <div id="selectionHead" class="head"/>;
		let selectionLabel;
		if (typeof init.selectionLabel == "string") selectionLabel = new Label().initialize({label: init.selectionLabel});
		else selectionLabel = new Label().initialize(init.selectionLabel);
		selectionHead.appendChild(selectionLabel);

		if (newBtn) selectionHead.appendChild(newBtn);
		this._removeAllBtn = <c-button î={{icon: "/@skin@/commons/icons/doubleLeft.svg", title: "Tout retirer"}} class="allAction" onclick={this._removeAll}/> as Button;
		selectionHead.appendChild(this._removeAllBtn);

		this._attach(this.localName, init, availableHead, this._availableGrid, toolbar, selectionHead, this._selectionGrid);
		reg.installSkin("scroll/small", this.shadowRoot);

		this._initializeForm(init);
		this._refresh();
	}

	protected _onLeft(this: Button) {
		if (this.disabled) return;
		const self = DOMSH.findHost<InputOrderedSet>(this);
		self._removeFromSelection();
	}

	protected _onRight(this: Button) {
		if (this.disabled) return;
		const self = DOMSH.findHost<InputOrderedSet>(this);
		const count = self._selection.countRows();
		self._addToSelection(count);
	}

	protected _onDblClick(this: void, row: IGridDataRow | null, ev: MouseEvent) {
		const grid = DOMSH.findHost(ev.target as HTMLElement);
		const self = DOMSH.findHost<InputOrderedSet>(grid);
		if (grid == self._availableGrid) self._addToSelection(self._selection.countRows());
		else if (grid == self._selectionGrid) self._removeFromSelection();
	}

	/** Déplacement d'une valeur disponible en valeur sélectionnée */
	protected _addToSelection(insertIndex: number) {
		const selected = this._available.getSelectedDatas();
		for (const sel of selected) this._available.deleteRowKey(sel);
		this._selection.updateDatas(insertIndex, 0, ...selected);
		this._selectionGrid.setSelectedRows([insertIndex, -(insertIndex + selected.length - 1)]);
		this._refresh();
		this._dispatchChange();
	}

	/** Suppresion d'une valeur sélectionné et réinsertion en valeur disponible */
	protected _removeFromSelection() {
		const selected = this._selection.getSelectedDatas();
		for (const sel of selected) this._selection.deleteRowKey(sel);
		// Utilisation de setDatas pour forcer le tri
		this._available.setDatas(this._available.getDatas().concat(selected));

		const available = this._available.getDatas();
		for (let i = 0; i < available.length; i++) {
			if (selected.includes(available[i])) this._availableGrid.addSelectedRows(i, i);
		}
		this._refresh();
		this._dispatchChange();
	}

	/** Déplacement vers le haut de l'ensemble de la sélection des valeurs sélectionnée */
	protected _onUp(this: Button) {
		if (this.disabled) return;
		const self = DOMSH.findHost<InputOrderedSet>(this);
		const selection = self._selection.getDatas();
		if (!selection.length || self._selectionGrid.isRowSelected(0)) return;
		const newSelection = [];
		const selectedOffsets = [];
		let i = 0;
		while (i < selection.length) {
			const data = selection[i];
			i++;
			while (self._selectionGrid.isRowSelected(i)) {
				newSelection.push(selection[i]);
				selectedOffsets.push(i - 1);
				i++;
			}
			newSelection.push(data);
		}
		self._selection.setDatas(newSelection);
		self._selectionGrid.setSelectedRows(selectedOffsets);
		self._dispatchChange();
	}

	/** Déplacement vers le bas de l'ensemble de la sélection des valeurs sélectionnée */
	protected _onDown(this: Button) {
		if (this.disabled) return;
		const self = DOMSH.findHost<InputOrderedSet>(this);
		const selection = self._selection.getDatas();
		if (!selection.length || self._selectionGrid.isRowSelected(selection.length - 1)) return;
		const newSelection = [];
		const selectedOffsets = [];
		let i = selection.length - 1;
		while (i >= 0) {
			const data = selection[i];
			i--;
			while (self._selectionGrid.isRowSelected(i)) {
				newSelection.unshift(selection[i]);
				selectedOffsets.push(i + 1);
				i--;
			}
			newSelection.unshift(data);
		}
		self._selection.setDatas(newSelection);
		self._selectionGrid.setSelectedRows(selectedOffsets);
		self._dispatchChange();
	}

	protected _addAll(this: Button) {
		if (this.disabled) return;
		const self = DOMSH.findHost<InputOrderedSet>(this);
		self._availableGrid.setSelectedRows([0, -(self._availableGrid.dataHolder.countRows() - 1)]);
		self._onRight.call(this);
	}

	protected _removeAll(this: Button) {
		if (this.disabled) return;
		const self = DOMSH.findHost<InputOrderedSet>(this);
		self._selectionGrid.setSelectedRows([0, -(self._selectionGrid.dataHolder.countRows() - 1)]);
		self._onLeft.call(this);
	}

	protected _onGridSelect(this: Grid) {
		DOMSH.findHost<InputOrderedSet>(this)._refresh();
	}

	protected _dispatchChange() {
		this.dispatchEvent(new CustomEvent("input", {bubbles: true, cancelable: false}));
		this.dispatchEvent(new CustomEvent("change", {bubbles: true, cancelable: false}));
	}

	protected _refresh() {
		const hasSelection = this._selection.getSelectedDatas().length;
		this._leftBtn.disabled = !hasSelection;
		this._rightBtn.disabled = !this._available.getSelectedDatas().length;
		this._upBtn.disabled = !hasSelection || this._selectionGrid.isRowSelected(0);
		this._downBtn.disabled = !hasSelection || this._selectionGrid.isRowSelected(this._selection.countRows() - 1);
		this._addAllBtn.disabled = !this._availableGrid.dataHolder.countRows();
		this._removeAllBtn.disabled = !this._selectionGrid.dataHolder.countRows();
		this._refreshValidity();
	}

	protected _refreshValidity(): void {
		const values = this.value;
		if (this.required && !this.value.length) this.setValidity({valueMissing: true}, "Veuillez sélectionner un élément.", this._selectionGrid);
		else if (this._params.checkValidity) {
			const result = this._params.checkValidity.call(this, values);
			if (result == true) {
				this.setValidity({});
			} else {
				if (typeof result == "string")
					this.setValidity({valueMissing: true}, result);
				else
					this.setValidity({valueMissing: true}, "Des groupes sélectionnés sont incompatibles");
			}
		} else this.setValidity({});
	}

	/** Initialisation du drag */
	protected _initDrag(grid: GridSmall) {
		grid.setRowDropMgr(this);
		grid.linesNode.setAttribute('draggable', 'true');
		grid.addEventListener('dragstart', function (this: GridSmall, ev) {
			const self = DOMSH.findHost<InputOrderedSet>(this);
			ev.dataTransfer.effectAllowed = 'move';
			ev.dataTransfer.setDragImage(this._getLine(this.getSelectedRow()), 0, 0);
			self.dragFrom = this;

		});
		grid.addEventListener('dragend', function (ev) {
			const self = DOMSH.findHost<InputOrderedSet>(this);
			self.dragFrom = null;
		});

	}

	onDragOverRow(ev: DragEvent, grid: IGrid, row: GridDataRowJson<IOrderedSetData>, line: HTMLElement): EGridDropPos | EGridDropPos {
		// Le drag n'est pas autorisé à l'intérieur des valeurs disponible
		if (!this.dragFrom || (grid == this._availableGrid && this.dragFrom == this._availableGrid)) {
			ev.dataTransfer.dropEffect = 'none';
			return EGridDropPos.none;
		}
		// Déplacement à l'intérieur de la sélection désactivé sur elle-même
		if (grid == this._selectionGrid && this.dragFrom == grid && row) {
			const offset = this._selection.getOffset(row.rowKey);
			if (grid.isRowSelected(offset)) {
				ev.dataTransfer.dropEffect = 'none';
				return EGridDropPos.none;
			}
		}
		ev.dataTransfer.dropEffect = 'move';
		return row ? EGridDropPos.before | EGridDropPos.after : EGridDropPos.after;
	}

	dropOnRow(ev: DragEvent, grid: IGrid, row: GridDataRowJson<IOrderedSetData>, line: HTMLElement, pos: EGridDropPos): void {
		if (grid == this._selectionGrid) {
			let insertIndex = row ? this._selection.getOffset(row.rowKey) : this._selection.countRows() - 1;
			if (pos === EGridDropPos.after) insertIndex++;
			if (this.dragFrom != grid) {
				this._addToSelection(insertIndex);
			} else {
				const selected = this._selection.getSelectedDatas();
				this._selection.setDatas(this._selection.getUnselectedDatas());
				insertIndex = Math.min(insertIndex, this._selection.countRows());
				this._selection.updateDatas(insertIndex, 0, ...selected);
				this._selectionGrid.setSelectedRows([insertIndex, -(insertIndex + selected.length - 1)]);
				this._dispatchChange();
			}
		} else if (grid == this._availableGrid && this.dragFrom != grid) {
			this._removeFromSelection();
		}
	}

	redrawLine(row: GridDataRowJson<any>, line: HTMLElement): void {
		if (row.rowDatas.notAvailable) line.classList.add('notAvailable');
	}
}


REG.reg.registerSkin('c-input-ordered-set', 1, /* language=CSS */ `
	:host {
		display: grid;
		grid-template-columns: 1fr min-content 1fr;
		grid-template-rows: min-content 1fr;
		grid-template-areas: "ah . sh" "ag tb sg";
		min-width: 10em;
		min-height: 6em;
	}

	c-grid-small[disabled] {
		pointer-events: none;
		opacity: .5;
	}

	.head {
		margin: .2em 0;
		display: flex;
		align-items: center;
	}

	.head > c-label {
		flex: 1;
		justify-content: start;
		font-weight: bold;
	}

	#selectionGrid {
		grid-area: sg;
	}

	#selectionHead {
		grid-area: sh;
	}

	#toolbar {
		grid-area: tb;
		display: flex;
		min-width: 0;
		min-height: 0;
		flex-direction: column;
		margin: 0 1ex;
	}

	.spacer {
		flex: 1;
	}

	#availableGrid {
		grid-area: ag;
	}

	#availableHead {
		grid-area: ah;
	}
`);

REG.reg.registerSkin("c-input-ordered-set/grid", 1, /* language=CSS */ `
	#resizers {
		display: none;
	}

	.line {
		padding: 0 .3ex;
	}

	.cellIndex {
		font-size: .8em;
		line-height: 1.2;
		font-weight: bold;
		filter: var(--fade-filter);
	}

	.notAvailable {
		font-style: italic;
	}

	#empty {
		font-style: italic;
		margin: 1ex;
		text-align: left;
		color: var(--fade-color);
	}
`);

customElements.define('c-input-ordered-set', InputOrderedSet);


export class InputOrderedSetPanel extends MxFormElement(BaseElement) {

	protected _params: OOrderedSetInit;

	protected _preview: HTMLDivElement;

	protected _value: string[] = [];

	protected _dataSet: Dict<IOrderedSetEntry>;

	protected _popup: IPopupable<string[]>;

	name: string;

	get value(): string[] {
		return this._value || [];
	}

	set value(vals: string[]) {
		let onEdit = this._popup != null;
		if (onEdit) this._popup.close();
		this._value = vals;
		this._refresh();
		if (onEdit) this._onEdit();
	}

	get dataSet(): Dict<IOrderedSetEntry> {
		return this._dataSet;
	}

	set dataSet(dataSet: Dict<IOrderedSetEntry>) {
		let onEdit = this._popup != null;
		if (onEdit) this._popup.close();
		this._dataSet = dataSet;
		this._refresh();
		if (onEdit) this._onEdit();
	}

	protected _initialize(init: OOrderedSetInit) {
		this._params = init;
		const reg = this.findReg(init);
		super._initialize(init);

		this._dataSet = init.dataSet;
		if (init.value) this._value = init.value;

		this._preview = <div class="preview" data-preview-mode={init.previewMode}/> as HTMLDivElement;
		this._attach(this.localName, init, this._preview);
		reg.installSkin("scroll/small", this.shadowRoot);

		this.tabIndex = 0;
		this.addEventListener('click', this._onEdit);
		BASIS.makeClickable(this);
		this._initializeForm(init);
		this._refresh();
	}

	protected async _onEdit() {
		const prevValJson = JSON.stringify(this.value);

		const set = new InputOrderedSet().initialize(this._params);
		const panel = <div id="panel">
			<ShadowJsx skin="c-input-ordered-set-panel/panel">
				{set}
				<div id="footer">
					<c-button id="validBtn" class="default" onclick={() => POPUP.findPopupableParent(panel).close(set.value)} ui-context="dialog">Valider</c-button>
					<c-button id="cancelBtn" onclick={() => POPUP.findPopupableParent(panel).close()} ui-context="dialog">Annuler</c-button>
				</div>
			</ShadowJsx>
		</div> as HTMLDivElement;
		set.value = this.value;
		set.dataSet = this.dataSet;

		this._popup = POPUP.showMenu<string[]>(panel, {elem: this, initWidth: '35em', initHeight: '15em', initMaxHeight: '50vh'}, this, {resizer: {}, skinOver: "c-input-ordered-set-panel/popup"});
		const newVal = await this._popup.onNextClose();
		this._popup = null;
		if (newVal) {
			/* Vérification de l'état dirty par comparaison du JSON (TODO pourrait être optimisé) */
			this._value = newVal;
			if (JSON.stringify(newVal) != prevValJson) this._dispatchChange();
			this._refresh();
		}
	}

	protected _dispatchChange() {
		this.dispatchEvent(new CustomEvent("input", {bubbles: true, cancelable: false}));
		this.dispatchEvent(new CustomEvent("change", {bubbles: true, cancelable: false}));
	}

	protected _refresh() {
		if (this._params.previewMode === "asLines") {
			this._preview.innerText = '';
			if (this.value.length > 0) {
				this.value.map((val) => {
					this._preview.appendChild(<div class="entry">{this.dataSet[val] ? this.dataSet[val].label : val}</div>)
				});
				DOM.removeClass(this._preview, "noEntry");
			} else {
				this._preview.innerText = this._params.emptySelectionMsg || "Aucune sélection";
				DOM.addClass(this._preview, "noEntry");
			}
		} else {
			if (this.value.length > 0) {
				this._preview.innerText = this.value.map((val) => this.dataSet[val] ? this.dataSet[val].label : val).join(", ");
				DOM.removeClass(this._preview, "noEntry");
			} else {
				this._preview.innerText = this._params.emptySelectionMsg || "Aucune sélection";
				DOM.addClass(this._preview, "noEntry");
			}
		}
		this._refreshValidity();
	}

	protected _refreshValidity() {
		if (this.required && !this._value.length) this.setValidity({valueMissing: true}, "Veuillez renseigner ce champ.");
		else if (this._params.checkValidity) {
			const result = this._params.checkValidity.call(this, this._value);
			if (result == true) {
				this.setValidity({});
			} else {
				if (typeof result == "string")
					this.setValidity({valueMissing: true}, result);
				else
					this.setValidity({valueMissing: true}, "Des groupes sélectionnés sont incompatibles");
			}
		} else this.setValidity({});
	}
}

REG.reg.registerSkin("c-input-ordered-set-panel", 1, /* language=CSS */ `
	:host {
		display: flex;
		min-width: 0;
		min-height: 0;
		background-color: var(--form-bgcolor);
		border: 1px solid var(--border-color);
		user-select: none;
	}

	:host(:disabled) {
		background-color: transparent;
	}


	:host(:focus) {
		outline: var(--focus-outline);
	}

	:host([indeterminate]) {
		opacity: .5;
	}

	.preview {
		flex: 1;
		overflow-y: auto;
		overflow-x: hidden;
		white-space: nowrap;
		text-overflow: ellipsis;
		min-width: 5rem;
		align-self: center;
		max-height: 4rem;
	}


	.preview[data-preview-mode='asLines'] {
		border-inline-end: 1px solid var(--border-color);
	}

	.preview[data-preview-mode='asLines'] > .entry {
		margin-inline-end: .2rem;
	}

	:host::after {
		content: var(--dropdown-url);
		display: flex;
		min-width: auto;
		min-height: auto;
		width: var(--icon-size);
		height: var(--icon-size);
		align-self: center;
		align-items: center;
		justify-content: center;
	}

	:host([disabled])::after {
		opacity: 0.2;
	}

	:host(:hover:not([disabled]))::after {
		filter: var(--hover-filter);
	}
`);

REG.reg.registerSkin("c-input-ordered-set-panel/popup", 1, /* language=CSS */ `
	#content {
		min-width: 11em;
		min-height: 10em;
	}
`);

REG.reg.registerSkin("c-input-ordered-set-panel/panel", 1, /* language=CSS */ `
	:host {
		display: flex;
		min-width: 0;
		min-height: 0;
		flex-direction: column;
		flex: 1;
	}

	c-input-ordered-set {
		flex: 1;
		padding: 1ex 1em 1em;
	}

	#footer {
		display: flex;
		min-height: fit-content;
		min-width: 0;
		align-items: center;
		justify-content: flex-end;
		border-top: 1px solid var(--border-color);
	}
`);

customElements.define('c-input-ordered-set-panel', InputOrderedSetPanel);
